import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { modifier } from 'ember-modifier';
import { PhDotsThree } from 'ember-phosphor-icons';
import Popover from './popover';
import Select from './select';
import type RouterService from '@ember/routing/router-service';

export interface NavTab {
  /** Display label for the tab */
  label: string;
  /** Ember route path */
  route: string;
  /** Optional model param passed to LinkTo */
  model?: unknown;
}

interface NavTabsSignature {
  Args: {
    /** Array of tab definitions */
    tabs: NavTab[];
    /** When true, pins the tabs to the top of the viewport on scroll with an opaque background. */
    sticky?: boolean;
    /**
     * Stable identity for automation: emitted as `data-test-hook` on the
     * root, as `<hook>__<route>` on every tab (visible and overflow), and
     * as `<hook>__mobile` on the small-screen Select — so a finding
     * attributes to a specific tab rather than to the surrounding route.
     * Routes are code identifiers, which makes the derived hooks stable
     * across relabeling and re-renders. See tuitionio/tio-front-end#10057.
     *
     * A call-site `data-test-hook` still overrides this on the root, and the
     * derived hooks follow it — root and tabs never split namespaces.
     *
     * @example <NavTabs @testHook="programs-nav" @tabs={{this.tabs}} />
     */
    testHook?: string;
  };
  Element: HTMLDivElement;
}

const TAB_BASE =
  'whitespace-nowrap border-b-2 px-1 py-3 tio-text-sm-medium transition-colors duration-150';

const TAB_INACTIVE = 'tio-nav-tabs__tab--inactive';

// Routes through --brand (tio-purple-600 light / tio-purple-300 dark) so
// the active tab keeps brand contrast in both modes — a raw violet-600
// atom doesn't lift in dark mode and reads too dark.
const TAB_ACTIVE = 'tio-nav-tabs__tab--active';

/**
 * Reimagined NavTabs — route-based navigation tabs with responsive overflow.
 *
 * Features:
 * - Route-based tabs via `<LinkTo>`
 * - Responsive overflow menu via reimagined Popover when tabs don't fit
 * - Mobile: collapses to a reimagined Select for small screens
 * - Matches reimagined design system (violet accent)
 *
 * @example
 * ```gts
 * import NavTabs from 'tio-ui/components/nav-tabs';
 *  *
 * <NavTabs
 *   @tabs={{array
 *     (hash label="Overview" route="authenticated.programs.show")
 *     (hash label="Details" route="authenticated.programs.details")
 *     (hash label="History" route="authenticated.programs.history")
 *   }}
 * />
 * ```
 */
class NavTabs extends Component<NavTabsSignature> {
  @service declare router: RouterService;
  @tracked visibleCount = 0;
  @tracked hasMeasured = false;
  @tracked renderedHook?: string;
  tabWidths: number[] = [];
  navElement?: HTMLElement;
  resizeObserver?: ResizeObserver;

  // ── Modifiers ───────────────────────────────────────────────────

  // Reads the root's hook back off the DOM so the value a call site set
  // through `...attributes` — which overrides the arg on the root, per the
  // primitive contract — becomes the namespace the derived hooks use too.
  // Deriving straight from `@testHook` would leave an overridden root and its
  // own tabs in two different namespaces.
  captureHook = modifier((element: HTMLElement) => {
    const rendered = element.getAttribute('data-test-hook') ?? undefined;
    if (rendered !== this.renderedHook) {
      this.renderedHook = rendered;
    }
  });

  setupNav = modifier((element: HTMLElement) => {
    this.navElement = element;

    // Use rAF to ensure DOM is painted before measuring
    requestAnimationFrame(() => {
      this.measureAndCalculate();
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.calculateVisibleTabs();
    });
    this.resizeObserver.observe(element);

    return () => {
      this.resizeObserver?.disconnect();
    };
  });

  // ── Measurement ─────────────────────────────────────────────────

  measureAndCalculate = () => {
    if (!this.navElement) return;

    const tabElements = this.navElement.querySelectorAll<HTMLElement>('[data-nav-tab]');
    this.tabWidths = Array.from(tabElements).map((el) => {
      const style = window.getComputedStyle(el);
      const marginLeft = Number.parseFloat(style.marginLeft) || 0;
      const marginRight = Number.parseFloat(style.marginRight) || 0;
      return el.offsetWidth + marginLeft + marginRight;
    });

    this.hasMeasured = true;
    this.calculateVisibleTabs();
  };

  calculateVisibleTabs = () => {
    if (!this.navElement || !this.hasMeasured) return;

    const containerWidth = this.navElement.offsetWidth;
    const overflowButtonWidth = 48;
    const gapWidth = 32; // gap-x-8 = 2rem
    let availableWidth = containerWidth - overflowButtonWidth;
    let count = 0;

    for (let i = 0; i < this.tabWidths.length; i++) {
      const tabWidth = this.tabWidths[i] ?? 0;
      const gapNeeded = i > 0 ? gapWidth : 0;
      if (availableWidth >= tabWidth + gapNeeded) {
        availableWidth -= tabWidth + gapNeeded;
        count++;
      } else {
        break;
      }
    }

    // If all tabs fit without an overflow button, show them all
    const totalWidth = this.tabWidths.reduce((sum, w, i) => sum + w + (i > 0 ? gapWidth : 0), 0);
    if (totalWidth <= containerWidth) {
      count = this.args.tabs.length;
    }

    // Always show at least 1 tab
    if (count === 0 && this.args.tabs.length > 0) {
      count = 1;
    }

    this.visibleCount = count;
  };

  // ── Actions ─────────────────────────────────────────────────────

  handleSelectChange = (key: string | null) => {
    if (!key) return;
    const tab = this.args.tabs.find((t) => t.route === key);
    if (tab) {
      if (tab.model) {
        this.router.transitionTo(tab.route, tab.model as object);
      } else {
        this.router.transitionTo(tab.route);
      }
    }
  };

  handleOverflowClick = (tab: NavTab) => {
    if (tab.model) {
      this.router.transitionTo(tab.route, tab.model as object);
    } else {
      this.router.transitionTo(tab.route);
    }
  };

  // ── Derived state ───────────────────────────────────────────────

  tabHook = (tab: NavTab) => {
    return this.rootHook ? `${this.rootHook}__${tab.route}` : undefined;
  };

  get mobileHook() {
    return this.rootHook ? `${this.rootHook}__mobile` : undefined;
  }

  // `@testHook` until the root is in the DOM, then whatever it actually
  // carries — one namespace for the root and every control under it.
  get rootHook() {
    return this.renderedHook ?? this.args.testHook;
  }

  get currentRoute() {
    return this.router.currentRouteName;
  }

  get selectItems() {
    return this.args.tabs.map((tab) => ({
      key: tab.route,
      label: tab.label,
    }));
  }

  get visibleTabs() {
    // Before measurement, show all so we can measure them
    if (!this.hasMeasured) {
      return this.args.tabs;
    }
    return this.args.tabs.slice(0, this.visibleCount);
  }

  get overflowTabs() {
    if (!this.hasMeasured) {
      return [];
    }
    return this.args.tabs.slice(this.visibleCount);
  }

  get hasOverflow() {
    return this.hasMeasured && this.overflowTabs.length > 0;
  }

  // ── Template ────────────────────────────────────────────────────

  <template>
    <div
      class="w-full {{if @sticky 'sticky top-0 z-30 tio-bg-surface'}}"
      data-test-hook={{@testHook}}
      ...attributes
      {{this.captureHook}}
    >
      {{! ── Mobile: Select dropdown (sm and below) ── }}
      <div class="sm:hidden border-b tio-border-default pb-4">
        <Select
          @items={{this.selectItems}}
          @selected={{this.currentRoute}}
          @onChange={{this.handleSelectChange}}
          @size="md"
          @clearable={{false}}
          @testHook={{this.mobileHook}}
        />
      </div>

      {{! ── Desktop: Tabs with overflow (sm+) ── }}
      <div class="hidden sm:block border-b tio-border-default">
        <nav class="-mb-px tio-row-flush gap-x-8" aria-label="Tabs" {{this.setupNav}}>
          {{#each this.visibleTabs as |tab|}}
            {{#if tab.model}}
              <LinkTo
                @route={{tab.route}}
                @model={{tab.model}}
                class="{{TAB_BASE}} {{if (eq tab.route this.currentRoute) TAB_ACTIVE TAB_INACTIVE}}"
                data-nav-tab
                data-test-hook={{this.tabHook tab}}
              >
                {{tab.label}}
              </LinkTo>
            {{else}}
              <LinkTo
                @route={{tab.route}}
                class="{{TAB_BASE}} {{if (eq tab.route this.currentRoute) TAB_ACTIVE TAB_INACTIVE}}"
                data-nav-tab
                data-test-hook={{this.tabHook tab}}
              >
                {{tab.label}}
              </LinkTo>
            {{/if}}
          {{/each}}

          {{#if this.hasOverflow}}
            <Popover @placement="bottom-end" @maxWidth={{220}}>
              <:trigger as |p|>
                <button
                  type="button"
                  class="p-2 tio-fg-muted hover:tio-fg-secondary transition-colors -mb-0.5"
                  aria-label="More tabs"
                  {{p.register}}
                  {{on "click" p.toggle}}
                >
                  <PhDotsThree @size={{20}} />
                </button>
              </:trigger>
              <:body>
                <div class="tio-col-flush py-1" role="menu" aria-label="More tabs">
                  {{#each this.overflowTabs as |tab|}}
                    <button
                      type="button"
                      class="text-left px-4 py-2 text-sm transition-colors
                        {{if
                          (eq tab.route this.currentRoute)
                          'tio-nav-tabs__overflow-option--active tio-text-sm-medium'
                          'tio-fg-secondary hover:tio-bg-sunken hover:tio-fg-primary'
                        }}"
                      data-test-hook={{this.tabHook tab}}
                      {{on "click" (fn this.handleOverflowClick tab)}}
                    >
                      {{tab.label}}
                    </button>
                  {{/each}}
                </div>
              </:body>
            </Popover>
          {{/if}}
        </nav>
      </div>
    </div>
  </template>
}

export default NavTabs;
